import { supabase } from '@/lib/supabase';

/**
 * Storage Service - Handles file uploads with Supabase Storage
 */

export const storageService = {
  /**
   * Upload a file to Supabase Storage
   * @param {File} file - File to upload
   * @param {string} bucket - Storage bucket name (default: 'report-photos')
   * @returns {Promise<{file_url: string, path: string}>}
   */
  async uploadFile(file, bucket = 'report-photos') {
    try {
      // Validate MIME type against allowlist — never trust the file extension alone
      const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        throw new Error(`File type "${file.type}" is not allowed. Please upload a JPEG, PNG, WebP, or GIF image.`);
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload file
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);

      return {
        file_url: publicUrl,
        path: filePath
      };
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error(`Failed to upload file: ${error.message}`);
    }
  },

  /**
   * Delete a file from Supabase Storage
   * @param {string} path - File path in storage
   * @param {string} bucket - Storage bucket name
   * @returns {Promise<void>}
   */
  async deleteFile(path, bucket = 'report-photos') {
    const { error } = await supabase.storage
      .from(bucket)
      .remove([path]);

    if (error) throw error;
  },

  /**
   * Get public URL for a file
   * @param {string} path - File path in storage
   * @param {string} bucket - Storage bucket name
   * @returns {string}
   */
  getPublicUrl(path, bucket = 'report-photos') {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);

    return data.publicUrl;
  },

  /**
   * Upload multiple files
   * @param {File[]} files - Array of files to upload
   * @param {string} bucket - Storage bucket name
   * @returns {Promise<Array<{file_url: string, path: string}>>}
   */
  async uploadMultiple(files, bucket = 'report-photos') {
    const uploads = files.map(file => this.uploadFile(file, bucket));
    return Promise.all(uploads);
  }
};

// Backward compatibility with Base44 integrations.Core.UploadFile
export const UploadFile = async ({ file }) => {
  return storageService.uploadFile(file);
};
